/**
 * TanStack Query hooks for Organization Users
 *
 * This file provides React Query hooks for fetching and mutating organization user data.
 * These hooks replace the async methods in the Zustand organization-users store.
 *
 * @module organization-users-queries
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  OrganizationInviteStatus,
  type OrganizationUser,
  OrganizationUserRole,
} from '../../entity/api/organization-user';
import type { ManageInvitationReq, ManageUserReq } from '../../entity/api/routes/organization';
import {
  getOrganizationUsersList,
  inviteUsersToOrganization,
  manageOrganizationInvitation,
  manageOrganizationUser,
  revokeInvite,
  transferOrganizationOwnership,
} from '../../sdk/organization-user';
import { getUserOrganizations } from '../../sdk/user';
import { setUsersInCache } from './users-queries';

// ============ CONSTANTS ============

export const ORG_USERS_STALE_MS = 1000 * 60 * 2; // 2 minutes

// ============ QUERY KEYS ============

export const organizationUsersKeys = {
  all: ['organization-users'] as const,
  byOrgId: function (orgId: string) {
    return [...organizationUsersKeys.all, 'org', orgId] as const;
  },
  userOrganizations: function () {
    return [...organizationUsersKeys.all, 'user-orgs'] as const;
  },
};

// ============ CACHE HELPERS (defined early for use in hooks) ============

/**
 * Get all cached organization users across all organizations
 * This iterates through all cached org users queries
 * Useful for checking pending invites across all organizations
 */
export function getAllOrganizationUsersFromCache(
  queryClient: ReturnType<typeof useQueryClient>
): OrganizationUser[] {
  const allOrgUsers: OrganizationUser[] = [];

  // Get all queries that match the organization-users pattern
  const queries = queryClient.getQueriesData<OrganizationUser[]>({
    queryKey: organizationUsersKeys.all,
  });

  for (const [, data] of queries) {
    if (data != null && Array.isArray(data)) {
      allOrgUsers.push(...data);
    }
  }

  return allOrgUsers;
}

// ============ QUERIES ============

/**
 * Shared queryFn for org users list.
 * Used by useOrganizationUsersList, prefetchOrganizationUsersList, and the route loader
 * so all three share one implementation and TanStack Query deduplicates concurrent calls.
 */
export async function orgUsersListQueryFn(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string
) {
  const response = await getOrganizationUsersList({ id: organizationId });

  if (response.users != null && response.users.length > 0) {
    setUsersInCache(queryClient, response.users);
  }

  return response.orgUsers;
}

/**
 * Fetch organization users list for a specific organization
 * Also populates individual users data in cache via setUsersInCache.
 *
 * Replaces: getStores().organizationUsers.getState().list(organizationId)
 */
export function useOrganizationUsersList(organizationId: string | undefined | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: organizationUsersKeys.byOrgId(organizationId ?? ''),
    queryFn: async function () {
      if (organizationId == null) {
        throw new Error('Organization ID is required');
      }
      return orgUsersListQueryFn(queryClient, organizationId);
    },
    enabled: organizationId != null && organizationId !== '',
    staleTime: ORG_USERS_STALE_MS,
  });
}

/**
 * Fetch all organizations the current user belongs to
 *
 * Replaces: getStores().organizationUsers.getState().getUserOrganizations()
 */
export function useUserOrganizations() {
  return useQuery({
    queryKey: organizationUsersKeys.userOrganizations(),
    queryFn: async function () {
      const response = await getUserOrganizations();
      return response.data;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Get organization users filtered by user ID from all cached organization user queries.
 * This is a derived/computed hook that reads from existing cache.
 *
 * Replaces: getStores().organizationUsers((s) => s.getByUserIdFromStore(userId))
 */
export function useOrganizationUsersByUserId(userId: string | undefined) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [...organizationUsersKeys.all, 'byUserId', userId ?? ''] as const,
    queryFn: function () {
      if (userId == null || userId === '') {
        return [];
      }
      // Get all cached organization user queries
      const allOrgUsers = getAllOrganizationUsersFromCache(queryClient);
      // Filter by user ID
      return allOrgUsers.filter(function (orgUser) {
        return orgUser?.userId === userId;
      });
    },
    enabled: userId != null && userId !== '',
    staleTime: 0, // Always recompute from cache
  });
}

/**
 * Get all organization users from cache (for listing purposes).
 * This reads from all cached org user queries.
 *
 * Replaces: getStores().organizationUsers((s) => s.listFromStore())
 */
export function useAllOrganizationUsers() {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [...organizationUsersKeys.all, 'allCached'] as const,
    queryFn: function () {
      return getAllOrganizationUsersFromCache(queryClient);
    },
    staleTime: 0, // Always recompute from cache
  });
}

/**
 * Derive the current user's role and OrganizationUser entry for a given org.
 * Shared between _tabs-layout and users.tsx to avoid duplicated useEffect derivation.
 * TanStack Query deduplicates the underlying useOrganizationUsersList subscription.
 */
export function useCurrentOrgUserRole(
  orgId: string,
  userId: string | undefined
): { isOwner: boolean; currentUserOrganization: OrganizationUser | undefined } {
  const { data: orgUsers } = useOrganizationUsersList(orgId);

  return useMemo<{ isOwner: boolean; currentUserOrganization: OrganizationUser | undefined }>(
    function () {
      const entry = orgUsers?.find(function (u) {
        return u?.userId === userId;
      });
      return {
        currentUserOrganization: entry,
        isOwner:
          entry?.role === OrganizationUserRole.Owner ||
          entry?.role === OrganizationUserRole.SuperAdmin,
      };
    },
    [orgUsers, userId]
  );
}

// ============ MUTATIONS ============

/**
 * Invite users to an organization
 *
 * Replaces: getStores().organizationUsers.getState().inviteUsersToOrganization(orgId, emails)
 */
export function useInviteUsersToOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      organizationId,
      userEmails,
    }: {
      organizationId: string;
      userEmails: string[];
    }) {
      const response = await inviteUsersToOrganization(
        { id: organizationId },
        userEmails.map(function (email) {
          return {
            userEmail: email,
            role: OrganizationUserRole.Member,
          };
        })
      );
      return response;
    },
    onSuccess: function (data, { organizationId }) {
      // Update the organization users cache
      queryClient.setQueryData(organizationUsersKeys.byOrgId(organizationId), data.orgUsers);

      // Also cache users individually by ID
      if (data.users != null && data.users.length > 0) {
        setUsersInCache(queryClient, data.users);
      }
    },
  });
}

/**
 * Manage an organization invitation (accept/reject)
 *
 * Replaces: getStores().organizationUsers.getState().manageOrganizationInvitation(orgId, status)
 */
export function useManageOrganizationInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      organizationId,
      status,
    }: {
      organizationId: string;
      status: ManageInvitationReq['body']['status'];
    }) {
      const response = await manageOrganizationInvitation({ id: organizationId }, { status });
      return response;
    },
    onSuccess: function (_, { organizationId }) {
      // Invalidate the organization users list to refetch
      void queryClient.invalidateQueries({
        queryKey: organizationUsersKeys.byOrgId(organizationId),
      });
      // Also invalidate user organizations since membership may have changed
      void queryClient.invalidateQueries({
        queryKey: organizationUsersKeys.userOrganizations(),
      });
    },
  });
}

/**
 * Manage an organization user (update role, remove, etc.)
 *
 * Replaces: getStores().organizationUsers.getState().manageOrganizationUser(orgId, data)
 */
export function useManageOrganizationUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      organizationId,
      data,
    }: {
      organizationId: string;
      data: ManageUserReq['body'];
    }) {
      const response = await manageOrganizationUser({ id: organizationId }, data);
      return response;
    },
    onSuccess: function (data, { organizationId }) {
      // Update the organization users cache
      queryClient.setQueryData(organizationUsersKeys.byOrgId(organizationId), data.orgUsers);

      // Also cache users individually by ID
      if (data.users != null && data.users.length > 0) {
        setUsersInCache(queryClient, data.users);
      }
    },
  });
}

/**
 * Revoke a user invitation
 *
 * Replaces: getStores().organizationUsers.getState().revokeInvite(orgId, userEmail)
 */
export function useRevokeInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      organizationId,
      userEmail,
    }: {
      organizationId: string;
      userEmail: string;
    }) {
      const response = await revokeInvite({ id: organizationId }, { userEmail });
      return response;
    },
    onSuccess: function (data, { organizationId }) {
      // Update the organization users cache
      queryClient.setQueryData(organizationUsersKeys.byOrgId(organizationId), data.orgUsers);
    },
  });
}

/**
 * Transfer organization ownership to another user
 *
 * Replaces: getStores().organizationUsers.getState().transferOwnership(orgId, toUserId)
 */
export function useTransferOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      organizationId,
      toUserId,
    }: {
      organizationId: string;
      toUserId: string;
    }) {
      const response = await transferOrganizationOwnership({ id: organizationId }, { toUserId });
      return response;
    },
    onSuccess: function (data, { organizationId }) {
      // Update the organization users cache
      queryClient.setQueryData(organizationUsersKeys.byOrgId(organizationId), data.orgUsers);

      // Also cache users individually by ID
      if (data.users != null && data.users.length > 0) {
        setUsersInCache(queryClient, data.users);
      }
    },
  });
}

// ============ CACHE HELPERS ============

/**
 * Get organization users from cache by organization ID (imperative access)
 */
export function getOrganizationUsersFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
): OrganizationUser[] | undefined {
  return queryClient.getQueryData<OrganizationUser[]>(organizationUsersKeys.byOrgId(orgId));
}

/**
 * Get user's organizations from cache (imperative access)
 */
export function getUserOrganizationsFromCache(
  queryClient: ReturnType<typeof useQueryClient>
): OrganizationUser[] | undefined {
  return queryClient.getQueryData<OrganizationUser[]>(organizationUsersKeys.userOrganizations());
}

/**
 * Filter organization users by user ID from cache
 * This is a client-side filter for when you need users by userId
 */
export function getOrganizationUsersByUserIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
  userId: string
): OrganizationUser[] {
  const orgUsers = getOrganizationUsersFromCache(queryClient, orgId);
  if (orgUsers == null) {
    return [];
  }
  return orgUsers.filter(function (ou) {
    return ou.userId === userId;
  });
}

/**
 * Invalidate all organization users queries
 */
export function invalidateOrganizationUsers(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: organizationUsersKeys.all });
}

/**
 * Invalidate organization users for a specific organization
 */
export function invalidateOrganizationUsersByOrgId(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: organizationUsersKeys.byOrgId(orgId),
  });
}

/**
 * Prefetch organization users list with same queryFn as useOrganizationUsersList.
 * Use in route loader so the hook sees fresh cache and does not refetch (avoids duplicate GET).
 */
export async function prefetchOrganizationUsersList(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string
): Promise<void> {
  await queryClient.prefetchQuery({
    queryKey: organizationUsersKeys.byOrgId(organizationId),
    queryFn: function () {
      return orgUsersListQueryFn(queryClient, organizationId);
    },
    staleTime: ORG_USERS_STALE_MS,
  });
}

/**
 * Fetch organization users list imperatively and cache it.
 * Uses fetchQuery so concurrent calls with the same orgId are deduplicated.
 * Use in router-auth.tsx or other non-React contexts.
 */
export async function fetchOrganizationUsersListImperative(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string
): Promise<OrganizationUser[]> {
  return queryClient.fetchQuery({
    queryKey: organizationUsersKeys.byOrgId(organizationId),
    queryFn: function () {
      return orgUsersListQueryFn(queryClient, organizationId);
    },
    staleTime: ORG_USERS_STALE_MS,
  });
}

/**
 * Fetch user's organizations imperatively and cache it
 * Use for non-React contexts like event handlers
 */
export async function fetchUserOrganizationsImperative(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<OrganizationUser[]> {
  const response = await getUserOrganizations();

  // Cache in TanStack Query
  queryClient.setQueryData(organizationUsersKeys.userOrganizations(), response.data);

  return response.data;
}

/**
 * Check if user has pending organization invites
 * This is a helper specifically for the auth flow
 */
export function hasPendingOrganizationInvite(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
  userEmail: string
): boolean {
  const allOrgUsers = getAllOrganizationUsersFromCache(queryClient);

  return allOrgUsers.some(function (orgUser) {
    return (
      orgUser?.inviteStatus === OrganizationInviteStatus.Pending &&
      (orgUser.userId === userId || orgUser.userEmail.toLowerCase() === userEmail.toLowerCase())
    );
  });
}
